import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, Sparkles, Clock } from 'lucide-react';
import { Button } from '../ui/button';

const PlansSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handlePlanClick = (planName) => {
    const phone = '5521976614158';
    const message = `Olá Hugo! Tenho interesse no plano ${planName}. Gostaria de saber mais detalhes.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const plans = [
    {
      name: 'Essencial',
      price: 'R$ 147',
      description: 'Para quem quer começar',
      popular: false,
      available: true,
      features: [
        '1 landing page profissional',
        'Hospedagem rápida e segura',
        'Site responsivo (mobile)',
        '1 alteração simples por mês',
        'Botão de contato direto no WhatsApp',
        'SEO básico (indexação no Google)',
        'Suporte básico',
      ],
    },
    {
      name: 'Profissional',
      price: 'R$ 197',
      description: 'Mais escolhido',
      popular: true,
      available: true,
      features: [
        'Tudo do plano Essencial',
        'SEO Avançado (configurações para ranqueamento)',
        'Compartilhamento otimizado nas redes sociais',
        'Até 3 alterações por mês',
        'Formulário integrado com WhatsApp',
        'Relatório básico de acessos',
      ],
    },
    {
      name: 'Growth',
      price: 'R$ 297',
      description: 'Em breve',
      popular: false,
      available: false,
      features: [
        'Tudo do plano Profissional',
        'Otimizações mensais de conversão',
        'Melhorias contínuas na página',
        'Integrações extras (formulários, CRM)',
        'Alterações ilimitadas (uso justo)',
        'Suporte prioritário',
      ],
    },
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-gray-900 to-black text-white" id="planos">
      <div className="container mx-auto px-6 lg:px-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Escolha o plano ideal para você
          </h2>
          <p className="text-xl text-gray-400">
            Sem custo inicial. Mensalidade acessível. Foque apenas em crescer.
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={plan.available ? { y: -10, scale: 1.02 } : {}}
              className={`relative rounded-3xl p-8 ${
                !plan.available
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 opacity-60'
                  : plan.popular
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 border-2 border-blue-400 shadow-2xl shadow-blue-500/50'
                  : 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700'
              } transition-all duration-300`}
            >
              {/* Popular Badge */}
              {plan.popular && plan.available && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg"
                  >
                    <Sparkles size={16} />
                    MAIS ESCOLHIDO
                  </motion.div>
                </div>
              )}

              {/* Coming Soon Badge */}
              {!plan.available && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="bg-gray-700 text-gray-300 px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg"
                  >
                    <Clock size={16} />
                    EM BREVE
                  </motion.div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8 mt-4">
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className={`text-sm mb-4 ${plan.popular && plan.available ? 'text-blue-100' : 'text-gray-400'}`}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className="text-5xl font-bold">{plan.price}</span>
                  <span className={`text-lg ${plan.popular && plan.available ? 'text-blue-100' : 'text-gray-400'}`}>/mês</span>
                </div>
                <Button
                  onClick={() => plan.available && handlePlanClick(plan.name)}
                  disabled={!plan.available}
                  className={`w-full py-6 rounded-full font-semibold text-lg transition-all duration-300 ${
                    !plan.available
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-white text-blue-600 hover:bg-gray-100 hover:scale-105'
                      : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105'
                  }`}
                >
                  {!plan.available ? 'Em Breve' : `Escolher ${plan.name}`}
                </Button>
              </div>

              {/* Features List */}
              <div className="space-y-4">
                {plan.features.map((feature, fIndex) => (
                  <motion.div
                    key={fIndex}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 + fIndex * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                      plan.popular && plan.available ? 'bg-white/20' : 'bg-blue-600/20'
                    }`}>
                      <Check className={plan.popular && plan.available ? 'text-white' : 'text-blue-400'} size={14} />
                    </div>
                    <p className={`text-sm ${plan.popular && plan.available ? 'text-blue-50' : 'text-gray-300'}`}>
                      {feature}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 px-8 py-4 rounded-full">
            <Check className="text-white" size={20} />
            <p className="text-white font-semibold">Sem taxa de adesão • Mensalidade acessível</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection;
